/**
 * HpModelEditor — shape: hp_model.
 * Covers DOMESTIC / COMMERCIAL / POOL / R290 / EVI heat pumps + the
 * HOT_WATER_TANK (commercial HWT template) family. Edits the shared `spec`
 * (title + KPI tiles + spec rows) PLUS a per-SKU `models` map: each model carries
 * { sku, modelLabel, heatingOnly?, values{} }. Unknown families with a `models`
 * Record (or `variants` for HWT) round-trip safely.
 *
 * SKU picker on the left, the selected model's fields on the right. The `values`
 * map is edited via the generic ValuesMapEditor so any custom per-model key is
 * editable without hardcoding every key.
 */

import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  CategorySpecEditor,
  FieldLabel,
  SpecInput,
  MiniLabel,
  ValuesMapEditor,
  type CategorySpecShape,
} from './specPrimitives';

interface HpModel {
  sku: string;
  modelLabel: string;
  heatingOnly?: boolean;
  values: Record<string, string>;
}

/** The HWT family stores its per-SKU rows under `variants` (a Record), every
 *  other hp_model family under `models`. Detect which key this data uses. */
function modelsKey(data: any): 'models' | 'variants' {
  if (data && typeof data === 'object' && data.models && typeof data.models === 'object') return 'models';
  if (data && typeof data === 'object' && data.variants && typeof data.variants === 'object' && !Array.isArray(data.variants))
    return 'variants';
  return 'models';
}

export function HpModelEditor({
  data,
  tokens,
  onChange,
}: {
  data: any;
  tokens: string[];
  onChange: (next: any) => void;
}) {
  const spec: CategorySpecShape = data?.spec ?? { title: '', kpiTiles: [], specRows: [] };
  const mKey = modelsKey(data);
  const models: Record<string, HpModel> =
    data?.[mKey] && typeof data[mKey] === 'object' && !Array.isArray(data[mKey]) ? data[mKey] : {};
  const skus = Object.keys(models);

  const [selectedSku, setSelectedSku] = useState<string>(skus[0] ?? '');
  // Keep the selection valid when the SKU set changes (e.g. after add/delete).
  useEffect(() => {
    if (skus.length && !models[selectedSku]) setSelectedSku(skus[0]);
  }, [skus.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSpec = (next: CategorySpecShape) => onChange({ ...data, spec: next });
  const setModels = (next: Record<string, HpModel>) => onChange({ ...data, [mKey]: next });

  const patchModel = (sku: string, p: Partial<HpModel>) =>
    setModels({ ...models, [sku]: { ...models[sku], ...p } });

  const removeModel = (sku: string) => {
    const next = { ...models };
    delete next[sku];
    setModels(next);
    if (selectedSku === sku) setSelectedSku(Object.keys(next)[0] ?? '');
  };

  const addModel = () => {
    const base = 'NEW-SKU';
    let key = base;
    let n = 1;
    while (key in models) key = `${base}-${n++}`;
    setModels({ ...models, [key]: { sku: key, modelLabel: key, values: {} } });
    setSelectedSku(key);
  };

  const current = models[selectedSku];

  return (
    <>
      <CategorySpecEditor spec={spec} tokens={tokens} onChange={setSpec} />

      <FieldLabel label="Models" hint="per-SKU values keyed by spec-row field">
        <div style={{ display: 'flex', gap: 12 }}>
          {/* SKU list */}
          <div
            style={{
              width: 180,
              flexShrink: 0,
              border: '1px solid var(--line)',
              borderRadius: 9,
              background: 'var(--ivory-0)',
              maxHeight: 360,
              overflowY: 'auto',
              padding: 6,
            }}
          >
            {skus.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: 8 }}>No models yet.</div>
            )}
            {skus.map((sku) => {
              const on = sku === selectedSku;
              return (
                <button
                  key={sku}
                  type="button"
                  onClick={() => setSelectedSku(sku)}
                  style={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                    padding: '7px 9px',
                    marginBottom: 3,
                    borderRadius: 7,
                    fontSize: 12,
                    fontWeight: on ? 700 : 500,
                    border: 'none',
                    cursor: 'pointer',
                    background: on ? 'var(--ink-1)' : 'transparent',
                    color: on ? 'var(--ivory-0)' : 'var(--ink-1)',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sku}>
                    {sku}
                  </span>
                  <Trash2
                    size={12}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Remove model "${sku}"?`)) removeModel(sku);
                    }}
                    style={{ flexShrink: 0, opacity: on ? 1 : 0.5 }}
                  />
                </button>
              );
            })}
            <button
              type="button"
              onClick={addModel}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                width: '100%',
                justifyContent: 'center',
                height: 30,
                marginTop: 4,
                borderRadius: 7,
                fontSize: 12,
                fontWeight: 600,
                border: '1px dashed var(--line-strong)',
                background: 'var(--ivory-0)',
                color: 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              <Plus size={13} /> Add model
            </button>
          </div>

          {/* selected model editor */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {!current ? (
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', padding: 12 }}>
                Select or add a model.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <MiniLabel>SKU</MiniLabel>
                    <SpecInput value={current.sku ?? ''} onChange={(v) => patchModel(selectedSku, { sku: v })} />
                  </div>
                  <div>
                    <MiniLabel>Model label</MiniLabel>
                    <SpecInput value={current.modelLabel ?? ''} onChange={(v) => patchModel(selectedSku, { modelLabel: v })} />
                  </div>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--ink-2)' }}>
                  <input
                    type="checkbox"
                    checked={!!current.heatingOnly}
                    onChange={(e) => patchModel(selectedSku, { heatingOnly: e.target.checked || undefined })}
                  />
                  Heating-only (suppresses the cooling-output row)
                </label>
                <div>
                  <MiniLabel>Values</MiniLabel>
                  <ValuesMapEditor
                    values={current.values ?? {}}
                    tokens={tokens}
                    onChange={(values) => patchModel(selectedSku, { values })}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </FieldLabel>
    </>
  );
}
